import {
  formatCourierPackageTag,
  getOrderAmountDisplay,
  getOrderFulfillmentMethod,
  useTranslation,
  formatPrice,
  OrderStatusBadge,
  isCourierAssignableOrder,
  isManualApprovalPending,
} from '@dukanti/shared';
import type { CourierOrderAssignmentSummary, StoreOrderRow, TranslationKey } from '@dukanti/shared';
import { ChevronRight } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { isUrgentOrder } from '../lib/is-urgent-order';

export interface OrdersTableProps {
  readonly rows: readonly StoreOrderRow[];
  readonly onRowClick: (orderId: string) => void;
  readonly selectedOrderIds?: ReadonlySet<string>;
  readonly canSelectOrders?: boolean;
  readonly onSelectionChange?: (orderId: string, selected: boolean) => void;
}

type OrderRowWithAssignment = StoreOrderRow & {
  readonly courier_assignment?: CourierOrderAssignmentSummary | null;
};

function courierProofStatusKey(assignment: CourierOrderAssignmentSummary): TranslationKey {
  if (assignment.proof_status === 'internally_confirmed') {
    return 'admin.orders.courierProof.confirmed' as TranslationKey;
  }
  if (
    assignment.proof_status === 'submitted' ||
    assignment.proof_status === 'awaiting_internal_confirmation'
  ) {
    return 'admin.orders.courierProof.awaitingConfirmation' as TranslationKey;
  }
  return 'admin.orders.courierProof.awaitingProof' as TranslationKey;
}

export function OrdersTable({
  rows,
  onRowClick,
  selectedOrderIds,
  canSelectOrders = false,
  onSelectionChange,
}: OrdersTableProps): React.JSX.Element {
  const { t, dir, lang } = useTranslation();

  const formatDate = (iso: string): string => {
    const d = new Date(iso);
    return new Intl.DateTimeFormat(lang, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).format(d);
  };

  const openOrder = (orderId: string): void => {
    onRowClick(orderId);
  };

  const handleRowKeyDown = (event: React.KeyboardEvent<HTMLTableRowElement>, orderId: string) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    openOrder(orderId);
  };

  return (
    <div
      dir={dir}
      data-testid="orders-table"
      className="border-border bg-card shadow-admin-card hidden overflow-hidden rounded-lg border md:block"
    >
      <Table>
        <TableHeader>
          <TableRow>
            {canSelectOrders ? (
              <TableHead className="w-12">
                <span className="sr-only">
                  {t('admin.orders.courierAssignment.bulkAssign' as never)}
                </span>
              </TableHead>
            ) : null}
            <TableHead>{t('admin.orders.columns.customer')}</TableHead>
            <TableHead>{t('admin.orders.columns.date')}</TableHead>
            <TableHead>{t('admin.orders.columns.items')}</TableHead>
            <TableHead>{t('admin.orders.columns.total')}</TableHead>
            <TableHead>{t('admin.orders.columns.status')}</TableHead>
            <TableHead className="text-end">{t('admin.orders.columns.action')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => {
            const urgent = isUrgentOrder(row);
            const assignment =
              row.delivery_method === 'pickup'
                ? null
                : ((row as OrderRowWithAssignment).courier_assignment ?? null);
            const amount = getOrderAmountDisplay(row);
            const isAssignable = isCourierAssignableOrder(row);
            const isApprovalPending = isManualApprovalPending(row);
            return (
              <TableRow
                key={row.id}
                tabIndex={0}
                role="link"
                aria-label={t('admin.orders.viewOrderAria', {
                  id: row.id.slice(0, 8),
                  customer: row.customer_name_snapshot,
                })}
                data-testid="orders-table-row"
                className="hover:bg-admin-surface-muted focus-visible:ring-admin-lime focus-visible:ring-offset-paper cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset"
                onClick={() => openOrder(row.id)}
                onKeyDown={(event) => handleRowKeyDown(event, row.id)}
              >
                {canSelectOrders ? (
                  <TableCell>
                    <input
                      type="checkbox"
                      className="border-input text-admin-lime focus:ring-admin-lime size-4 rounded"
                      checked={selectedOrderIds?.has(row.id) ?? false}
                      disabled={!isAssignable}
                      aria-label={
                        isAssignable
                          ? t('admin.orders.courierAssignment.assign' as never)
                          : t('admin.orders.courierAssignment.notEligible' as never)
                      }
                      title={
                        isAssignable
                          ? t('admin.orders.courierAssignment.assign' as never)
                          : t('admin.orders.courierAssignment.notEligible' as never)
                      }
                      onClick={(event) => event.stopPropagation()}
                      onKeyDown={(event) => event.stopPropagation()}
                      onChange={(event) => onSelectionChange?.(row.id, event.target.checked)}
                    />
                  </TableCell>
                ) : null}
                <TableCell data-testid="orders-table-row-customer">
                  <div className="min-w-0">
                    <div className="text-foreground truncate text-sm font-medium">
                      {row.customer_name_snapshot}
                    </div>
                    {assignment ? (
                      <div className="text-muted-foreground mt-1 space-y-1 text-xs">
                        <div className="truncate">
                          {t('admin.orders.courierAssignment.assignedTo' as never, {
                            name: assignment.courier_name,
                          })}
                        </div>
                        <div className="flex flex-wrap gap-1">
                          {assignment.package_tag ? (
                            <span>
                              {t('admin.orders.courierAssignment.packageTag' as never, {
                                tag: formatCourierPackageTag(assignment.package_tag),
                              })}
                            </span>
                          ) : null}
                          <span>{t(courierProofStatusKey(assignment))}</span>
                        </div>
                      </div>
                    ) : null}
                    {row.customer_phone_snapshot && (
                      <div className="text-muted-foreground truncate text-xs">
                        {row.customer_phone_snapshot}
                      </div>
                    )}
                  </div>
                </TableCell>
                <TableCell
                  className="text-muted-foreground whitespace-nowrap text-sm"
                  data-testid="orders-table-row-date"
                >
                  {formatDate(row.created_at)}
                </TableCell>
                <TableCell data-testid="orders-table-row-items">
                  <div className="text-muted-foreground whitespace-nowrap text-xs">
                    {t('admin.orders.itemCount', { count: row.item_count })}
                  </div>
                </TableCell>
                <TableCell
                  className="font-display text-saffron whitespace-nowrap text-sm font-semibold"
                  data-testid="orders-table-row-subtotal"
                >
                  {formatPrice(amount.amountCents, lang)}
                  {!amount.confirmed ? (
                    <span className="text-muted-foreground block whitespace-normal text-xs font-normal">
                      {t(amount.confirmed ? 'checkout.total' : 'fulfillment.legacySubtotal')}
                    </span>
                  ) : null}
                </TableCell>
                <TableCell data-testid="orders-table-row-status">
                  <div className="flex items-center gap-2">
                    {urgent && (
                      <span
                        data-testid="orders-table-row-urgent"
                        className="bg-honey inline-block h-2 w-2 rounded-full"
                        aria-label={t('admin.orders.urgentLabel')}
                        title={t('admin.orders.urgentLabel')}
                      />
                    )}
                    <OrderStatusBadge status={row.status} method={getOrderFulfillmentMethod(row)} />
                    <span className="text-muted-foreground text-xs">
                      {t(
                        getOrderFulfillmentMethod(row) === 'pickup'
                          ? 'fulfillment.pickup'
                          : 'fulfillment.delivery',
                      )}
                    </span>
                    {isApprovalPending ? (
                      <span
                        className="border-honey/40 bg-honey/10 text-foreground rounded-full border px-2 py-0.5 text-xs"
                        data-testid="orders-table-row-approval-pending"
                      >
                        {t('admin.orders.manualApproval.pendingBadge' as never)}
                      </span>
                    ) : null}
                  </div>
                </TableCell>
                <TableCell className="text-end">
                  <span className="inline-flex justify-end">
                    <ChevronRight
                      className="text-muted-foreground rtl:rotate-180"
                      size={16}
                      strokeWidth={2}
                      aria-hidden="true"
                    />
                  </span>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
